import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import instance from "../apiInstance";
import {
  formatBidDisplayName,
  formatBotBidDisplayName,
  getUserIdFromToken,
} from "../lib/formatBidDisplayName";
import config from "../config";
import { formatAuctionImageUrl, LUXURY_FALLBACK_IMAGE } from "../imageUrl";
import { trackPiece } from "../trackerBus";
import { useAuctionRoomTracker } from "../hooks/useAuctionRoomTracker";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faClock,
  faUsers,
  faGavel,
  faChevronLeft,
  faChevronRight,
  faTrophy,
  faBolt,
  faPlus,
  faFire,
  faStore,
  faTag,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import "./AuctionDetailLux.css";
import AuctionEndState, { resolveAuctionEndState } from "./AuctionEndState";
import { getAuctionRulesItems } from "../lib/auctionRules";

const formatUsd = (val) => {
  if (!val) return "$ 0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(val);
};

const dedupeBids = (list) => {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const deduped = [];
  const sorted = [...list].sort((a, b) => {
    const diff = Number(b.amount) - Number(a.amount);
    if (diff !== 0) return diff;
    return new Date(b.timestamp || 0) - new Date(a.timestamp || 0);
  });
  for (const b of sorted) {
    const key = Number(b.amount);
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(b);
    }
  }
  return deduped;
};

const AuctionDetailLux = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const authToken = useSelector((state) => state.auth?.token);
  const currentUserId = getUserIdFromToken(authToken);
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState("");
  const [userTyped, setUserTyped] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState("");
  const [liveFlash, setLiveFlash] = useState(false);
  const [latestBidId, setLatestBidId] = useState(null);
  const [latestBidArrived, setLatestBidArrived] = useState(false);
  const intervalRef = useRef(null);
  const expiredSyncRef = useRef(false);

  const { trackMovement } = useAuctionRoomTracker(
    auction ? { id: auction.id || id, title: auction.title, current_price: auction.current_price } : null,
  );

  const fetchAuctionDetails = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      const response = await instance.get(`/auctions/${id}`);
      const data = response.data;
      if (data && data.bids) {
        data.bids = dedupeBids(data.bids);
      }
      setAuction((prev) => {
        if (prev && data && data.bids && data.bids.length > 0) {
          const prevTop = prev.bids?.[0]?.amount;
          const newTop = data.bids?.[0]?.amount;
          if (newTop && (!prevTop || Number(newTop) > Number(prevTop))) {
            const topBid = data.bids[0];
            const bidIdentifier = topBid._id || topBid.id || topBid.timestamp || Date.now();
            setLatestBidId(bidIdentifier);
            setLatestBidArrived(true);
            setLiveFlash(true);
            setTimeout(() => setLatestBidArrived(false), 3500);
            setTimeout(() => setLiveFlash(false), 1200);
          }
        }
        return data;
      });
      const minBid = (data?.current_price || 0) + (data?.increment || 5);
      setBidAmount((prev) => {
        if (!prev || !userTyped || parseFloat(prev) < minBid) {
          return minBid.toFixed(2);
        }
        return prev;
      });
    } catch (error) {
      if (isInitial) {
        toast.error("Erro ao carregar leilão");
        navigate("/auctions");
      }
    } finally {
      if (isInitial) setLoading(false);
    }
  }, [id, navigate, userTyped]);

  // Analytics de visualização
  useEffect(() => {
    if (!auction) return;
    const img = Array.isArray(auction.images) ? auction.images[currentImageIndex] : "";
    if (img) {
      trackPiece("auction_view", {
        button: "view",
        auctionId: auction.id || id,
        title: auction.title,
        url: img,
        price: auction.current_price,
        photoIndex: currentImageIndex,
        photoTotal: auction.images?.length ?? 0,
      });
    }
  }, [auction, currentImageIndex, id]);

  // 1. Conexão SSE Instantânea (Tempo Real em Milissegundos)
  useEffect(() => {
    let sse;
    try {
      const sseUrl = `${config.API_URL}/auctions/${id}/stream`;
      sse = new EventSource(sseUrl);

      sse.addEventListener("new_bid", (event) => {
        try {
          const data = JSON.parse(event.data);
          const inc = Number(data.increment || 50);
          const bidVal = Number(data.amount);
          const newMin = bidVal + inc;
          const bidIdentifier = data.timestamp || Date.now();

          // Animação de pulso ao vivo e destaque de novo lance no feed
          setLiveFlash(true);
          setLatestBidId(bidIdentifier);
          setLatestBidArrived(true);
          setTimeout(() => setLiveFlash(false), 1200);
          setTimeout(() => setLatestBidArrived(false), 3500);

          const displayName = data.user_id
            ? formatBidDisplayName(data.user_name)
            : formatBotBidDisplayName(data.user_name);
          const isSelfBid = Boolean(
            data.is_self
            || (data.user_id && currentUserId && String(data.user_id) === String(currentUserId)),
          );

          setAuction((prev) => {
            if (!prev) return prev;
            const newBidObj = {
              id: bidIdentifier,
              user_name: isSelfBid ? "Você" : displayName,
              amount: bidVal,
              timestamp: data.timestamp || new Date().toISOString(),
              is_self: isSelfBid,
            };
            const updatedBids = dedupeBids([newBidObj, ...(prev.bids || [])]);
            return {
              ...prev,
              current_price: bidVal,
              total_bids: updatedBids.length,
              bids: updatedBids,
            };
          });

          // Atualizar o input se estiver abaixo do novo lance mínimo
          setBidAmount((prev) => {
            const val = parseFloat(prev);
            if (isNaN(val) || val < newMin || !userTyped) {
              return newMin.toFixed(2);
            }
            return prev;
          });

          if (!isSelfBid) {
            trackMovement("sse_bid_seen", {
              sseAmount: bidVal,
              price: bidVal,
              button: displayName,
            });
          }

          toast(`🔥 Novo lance de ${isSelfBid ? "Você" : displayName}: R$ ${bidVal.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`, {
            icon: "⚡",
            duration: 3500,
          });
        } catch (err) {
          console.error("Erro ao processar SSE:", err);
        }
      });

      sse.onerror = () => {
        // Fallback para reconexão automática do navegador
      };
    } catch (e) {
      console.error("SSE não suportado:", e);
    }

    return () => {
      if (sse) sse.close();
    };
  }, [id, userTyped, currentUserId, trackMovement]);

  // 2. Polling de backup (a cada 2.5s) para garantir sincronismo
  useEffect(() => {
    fetchAuctionDetails(true);
    const pollInterval = setInterval(() => {
      fetchAuctionDetails(false);
    }, 2500);

    return () => {
      clearInterval(pollInterval);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [id, fetchAuctionDetails]);

  useEffect(() => {
    expiredSyncRef.current = false;
  }, [id]);

  // 3. Cronômetro regressivo
  useEffect(() => {
    if (auction?.end_time) {
      updateTimeRemaining();
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(updateTimeRemaining, 1000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auction?.end_time]);

  const updateTimeRemaining = () => {
    if (!auction?.end_time) return;
    const diff = new Date(auction.end_time) - new Date();
    if (diff <= 0) {
      setTimeRemaining("Encerrado");
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (!expiredSyncRef.current) {
        expiredSyncRef.current = true;
        fetchAuctionDetails(false);
      }
      return;
    }
    const d = Math.floor(diff / 864e5);
    const h = Math.floor((diff % 864e5) / 36e5);
    const m = Math.floor((diff % 36e5) / 6e4);
    const s = Math.floor((diff % 6e4) / 1e3);
    setTimeRemaining(d > 0 ? `${d}d ${h}h ${m}m ${s}s` : `${h}h ${m}m ${s}s`);
  };

  const handlePlaceBid = async (e, customAmount = null) => {
    if (e) e.preventDefault();
    const targetVal = customAmount !== null ? customAmount : parseFloat(bidAmount);
    const minBid = auction.current_price + (auction.increment || 5);

    if (isNaN(targetVal) || targetVal < minBid) {
      toast.error(`Lance mínimo atual: R$ ${minBid.toFixed(2)}`, { icon: "⚠️" });
      setBidAmount(minBid.toFixed(2));
      return;
    }

    try {
      setSubmitting(true);
      const response = await instance.post(`/auctions/${id}/bid`, { amount: targetVal });
      if (response.data.success) {
        toast.success("Lance realizado com sucesso! 🎉", { icon: "🔨" });
        trackPiece("bid_placed", {
          button: "bid",
          auctionId: auction.id || id,
          title: auction.title,
          price: targetVal,
        });
        setLiveFlash(true);
        setLatestBidArrived(true);
        setTimeout(() => setLatestBidArrived(false), 3500);
        setTimeout(() => setLiveFlash(false), 1200);
        setUserTyped(false);
        const nextSuggested = targetVal + (auction.increment || 5);
        setBidAmount(nextSuggested.toFixed(2));
        await fetchAuctionDetails(false);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Erro ao fazer lance";

      // Tratamento inteligente: Se outro participante cobriu no mesmo segundo
      if (detail.includes("mínimo R$") || detail.includes("consecutivos")) {
        const match = detail.match(/R\$\s*([\d.,]+)/i);
        if (match) {
          const rawNum = match[1].replace(".", "").replace(",", ".");
          const parsedMin = parseFloat(rawNum);
          if (!isNaN(parsedMin)) {
            setBidAmount(parsedMin.toFixed(2));
            trackMovement("bid_outbid", {
              bidAmount: targetVal,
              price: parsedMin,
            });
            toast(`⚡ Outro participante cobriu o lance! Atualizamos seu valor para R$ ${parsedMin.toFixed(2)}. Clique em Dar Lance para cobrir!`, {
              icon: "⚡",
              duration: 4500,
            });
            await fetchAuctionDetails(false);
            return;
          }
        }
      }
      trackMovement("bid_failed", {
        bidAmount: targetVal,
        button: String(detail).slice(0, 120),
      });
      toast.error(detail);
      await fetchAuctionDetails(false);
    } finally {
      setSubmitting(false);
    }
  };

  const setQuickBid = (addAmount) => {
    setUserTyped(true);
    const minBid = (auction?.current_price || 0) + (auction?.increment || 5);
    const newVal = minBid + addAmount;
    setBidAmount(newVal.toFixed(2));
    trackMovement("bid_quick_tap", {
      addAmount,
      bidAmount: newVal,
      button: `+${addAmount}`,
    });
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(price);

  const formatDateTime = (dateString) =>
    new Date(dateString).toLocaleString("pt-BR");

  if (loading) {
    return (
      <div className="adet-lux-root">
        <div className="adet-lux-loading">
          <div className="adet-lux-spinner" />
          Carregando leilão...
        </div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="adet-lux-root">
        <div className="adet-lux-loading">Leilão não encontrado</div>
      </div>
    );
  }

  const isActive = !resolveAuctionEndState(auction).closed;
  const minBid = auction.current_price + (auction.increment || 5);
  const rawImages = Array.isArray(auction.images) && auction.images.length > 0
    ? auction.images
    : [LUXURY_FALLBACK_IMAGE];
  const galleryImages = rawImages.map(formatAuctionImageUrl);

  return (
    <div className="adet-lux-root">
      {/* Header */}
      <div className="adet-lux-header">
        <button className="adet-lux-back" aria-label="Voltar" onClick={() => navigate("/auctions")}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Detalhes do Leilão</h2>
      </div>

      {/* Gallery */}
      <div className="adet-lux-gallery">
        <div className="adet-lux-gallery-inner">
          <img
            src={galleryImages[currentImageIndex] || LUXURY_FALLBACK_IMAGE}
            alt={auction.title}
            onError={(e) => {
              e.target.src = LUXURY_FALLBACK_IMAGE;
            }}
          />
          {galleryImages.length > 1 && (
            <>
              <button
                className="adet-lux-gallery-btn prev"
                aria-label="Imagem anterior"
                onClick={() => setCurrentImageIndex((p) => (p === 0 ? galleryImages.length - 1 : p - 1))}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                className="adet-lux-gallery-btn next"
                aria-label="Próxima imagem"
                onClick={() => setCurrentImageIndex((p) => (p === galleryImages.length - 1 ? 0 : p + 1))}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </>
          )}
        </div>
        {galleryImages.length > 1 && (
          <div className="adet-lux-dots">
            {galleryImages.map((_, idx) => (
              <span
                key={idx}
                className={idx === currentImageIndex ? "active" : ""}
                onClick={() => setCurrentImageIndex(idx)}
              />
            ))}
          </div>
        )}
        {galleryImages.length > 1 && (
          <div className="adet-lux-thumbnails-strip">
            {galleryImages.map((img, idx) => (
              <button
                key={idx}
                type="button"
                className={`adet-lux-thumb-item ${idx === currentImageIndex ? "active" : ""}`}
                onClick={() => setCurrentImageIndex(idx)}
                aria-label={`Ver foto ${idx + 1}`}
              >
                <img
                  src={img}
                  alt={`Ângulo ${idx + 1}`}
                  onError={(e) => {
                    e.target.src = LUXURY_FALLBACK_IMAGE;
                  }}
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="adet-lux-content">
        {/* Title + Timer */}
        <div className="adet-lux-title-section">
          <h1>{auction.title}</h1>
          <div className="adet-lux-timer-badge">
            <FontAwesomeIcon icon={faClock} />
            <span>{timeRemaining}</span>
          </div>
        </div>

        {/* Bid Info */}
        <div className={`adet-lux-bid-info ${liveFlash ? "pulse-live" : ""}`}>
          <div className="adet-lux-current-bid">
            <span className="label">
              Lance Atual {liveFlash && <span className="live-pill"><FontAwesomeIcon icon={faFire} /> Ao Vivo</span>}
            </span>
            <span className="value">{formatPrice(auction.current_price)}</span>
          </div>
          {auction.bids?.length > 0 && (
            <div className="adet-lux-highest">
              <FontAwesomeIcon icon={faTrophy} />
              <span>
                Maior lance: {auction.bids[0].is_self || auction.bids[0].user_name === "Você" ? "Você 🏆" : auction.bids[0].user_name}
              </span>
            </div>
          )}
          <div className="adet-lux-stats">
            <div className="adet-lux-stat">
              <FontAwesomeIcon icon={faUsers} />
              <span>{auction.total_bids} lances</span>
            </div>
            <div className="adet-lux-stat">
              <span className="adet-lux-category-badge">{auction.category}</span>
            </div>
          </div>
        </div>

        {/* Referência da Boutique Oficial & Ancoragem de Preço */}
        {auction.store_price && (
          <div className="adet-lux-store-ref-card">
            <div className="adet-lux-store-ref-header">
              <div className="adet-lux-store-ref-title">
                <div className="store-ref-icon-wrap">
                  <FontAwesomeIcon icon={faStore} className="store-ref-icon" />
                </div>
                <div>
                  <span className="store-ref-brand">{auction.official_brand || "Boutique Oficial"}</span>
                  <span className="store-ref-label">{auction.official_label || "Referência Oficial de Mercado"}</span>
                </div>
              </div>
              {auction.discount_percent > 0 && (
                <div className="adet-lux-discount-pill">
                  <FontAwesomeIcon icon={faTag} /> -{auction.discount_percent}% OFF
                </div>
              )}
            </div>

            <div className="adet-lux-store-ref-grid">
              <div className="adet-lux-ref-item">
                <span className="ref-item-lbl">Preço Loja (USD)</span>
                <span className="ref-item-val usd">
                  {formatUsd(auction.store_price_usd || (auction.store_price / (auction.usd_rate || 5.4)))}
                </span>
                <span className="ref-item-sub">Câmbio: R$ {(auction.usd_rate || 5.4).toFixed(2)}</span>
              </div>
              <div className="adet-lux-ref-item">
                <span className="ref-item-lbl">Preço no Brasil</span>
                <span className="ref-item-val store-crossed">{formatPrice(auction.store_price)}</span>
                <span className="ref-item-sub">Valor de vitrine</span>
              </div>
              <div className="adet-lux-ref-item highlight-savings">
                <span className="ref-item-lbl">Economia no Leilão</span>
                <span className="ref-item-val savings">
                  {formatPrice(auction.savings_amount || (auction.store_price - auction.current_price))}
                </span>
                <span className="ref-item-sub">Vantagem estimada</span>
              </div>
            </div>

            {auction.official_store_url && (
              <a
                href={auction.official_store_url}
                target="_blank"
                rel="noopener noreferrer"
                className="adet-lux-official-link-btn"
              >
                <span>Acessar vitrine oficial da {auction.official_brand || "marca"}</span>
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </a>
            )}
          </div>
        )}

        {/* Description */}
        <div className="adet-lux-desc-section">
          <h3>Descrição</h3>
          <p>{auction.description}</p>
        </div>

        {/* Regrinhas do leilão (mesma copy do WPP) */}
        <div className="adet-lux-rules-section">
          <h3>Regrinhas do leilão</h3>
          <ul className="adet-lux-rules-list">
            {getAuctionRulesItems(auction.increment).map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ul>
          <p className="adet-lux-rules-commitment">
            Arremate é compromisso de compra.
          </p>
        </div>

        {/* Aviso de Disputa em Tempo Real */}
        {isActive && (
          <div className="adet-lux-live-advice-banner">
            <div className="advice-pulse-icon">⚡</div>
            <div className="advice-content">
              <strong>Mantenha o app aberto para acompanhar!</strong>
              <p>Os lances acontecem em tempo real. Fique na tela para cobrir ofertas e garantir a sua peça nos minutos finais.</p>
            </div>
          </div>
        )}

        {/* Bid Form */}
        {isActive ? (
          <div className="adet-lux-form-section">
            <div className="adet-lux-form-header">
              <h3>Fazer Lance</h3>
              <span className="adet-lux-live-indicator">
                <span className="live-dot" /> Sincronizado ao vivo
              </span>
            </div>

            {/* Botões de Lance Rápido */}
            <div className="adet-lux-quick-bids">
              <button
                type="button"
                className="adet-lux-quick-btn active-quick"
                onClick={() => {
                  setUserTyped(true);
                  setBidAmount(minBid.toFixed(2));
                  trackMovement("bid_quick_tap", {
                    button: "cover",
                    bidAmount: minBid,
                  });
                }}
              >
                <FontAwesomeIcon icon={faBolt} /> Cobrir ({formatPrice(minBid)})
              </button>
              <button
                type="button"
                className="adet-lux-quick-btn"
                onClick={() => setQuickBid(100)}
              >
                <FontAwesomeIcon icon={faPlus} /> R$ 100
              </button>
              <button
                type="button"
                className="adet-lux-quick-btn"
                onClick={() => setQuickBid(200)}
              >
                <FontAwesomeIcon icon={faPlus} /> R$ 200
              </button>
              <button
                type="button"
                className="adet-lux-quick-btn"
                onClick={() => setQuickBid(300)}
              >
                <FontAwesomeIcon icon={faPlus} /> R$ 300
              </button>
            </div>

            <form onSubmit={handlePlaceBid}>
              <div className="adet-lux-form-row">
                <div className="adet-lux-form-field">
                  <label>Seu Lance</label>
                  <div className="adet-lux-form-input-wrap">
                    <span className="currency">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      min={minBid}
                      value={bidAmount}
                      onFocus={() => {
                        trackMovement("bid_input_focus", {
                          bidAmount: parseFloat(bidAmount) || minBid,
                        });
                      }}
                      onChange={(e) => {
                        setUserTyped(true);
                        setBidAmount(e.target.value);
                      }}
                      placeholder="0.00"
                      disabled={submitting}
                    />
                  </div>
                  <div className="adet-lux-input-helper">
                    <span>Mínimo obrigatório: <strong>{formatPrice(minBid)}</strong></span>
                    {userTyped && parseFloat(bidAmount) > minBid && (
                      <span className="above-min">+ {formatPrice(parseFloat(bidAmount) - minBid)} acima</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="submit"
                className="adet-lux-submit-btn"
                disabled={submitting}
              >
                <FontAwesomeIcon icon={faGavel} />
                {submitting ? "Enviando Lance..." : `Dar Lance de ${formatPrice(parseFloat(bidAmount) || minBid)}`}
              </button>
            </form>
            {auction.user_highest_bid && (
              <p className="adet-lux-user-bid">
                Seu maior lance neste leilão: {formatPrice(auction.user_highest_bid)}
              </p>
            )}
          </div>
        ) : (
          <AuctionEndState auction={auction} />
        )}

        {/* Bid History */}
        <div className="adet-lux-history-section">
          <div className="adet-lux-history-header">
            <div className="adet-lux-history-title-group">
              {isActive && (
                <div className="adet-lux-live-pulse-badge" title="Feed atualizado em tempo real via SSE">
                  <div className="adet-lux-live-dot-wrap">
                    <span className="adet-lux-live-ping" />
                    <span className="adet-lux-live-dot-green" />
                  </div>
                  <span className="adet-lux-live-text">AO VIVO</span>
                </div>
              )}
              <h3>Histórico de Lances</h3>
            </div>
            <div className="adet-lux-history-count-wrap">
              {latestBidArrived && <span className="adet-lux-new-bid-alert">Novo Lance!</span>}
              <span className="adet-lux-history-count">{auction.bids?.length || 0} lances</span>
            </div>
          </div>
          {auction.bids?.length > 0 ? (
            auction.bids.slice(0, 20).map((bid, idx) => {
              const isSelf = Boolean(bid.is_self || bid.user_name === "Você");
              const bidIdentifier = bid._id || bid.id || bid.timestamp;
              const isJustArrived = bidIdentifier === latestBidId || (idx === 0 && latestBidArrived);
              return (
                <div
                  key={bidIdentifier || idx}
                  className={`adet-lux-bid-item ${isSelf ? "adet-lux-bid-item-self" : ""} ${idx === 0 ? "adet-lux-bid-winner" : ""} ${isJustArrived ? "adet-lux-bid-just-arrived" : ""}`}
                >
                  <div className="adet-lux-bid-user">
                    {idx === 0 ? (
                      <FontAwesomeIcon icon={faTrophy} className="winner-icon" />
                    ) : (
                      <span className="adet-lux-bid-bullet">•</span>
                    )}
                    <span className={`adet-lux-bid-name ${isSelf ? "self-name" : ""}`}>
                      {isSelf ? "Você" : bid.user_name}
                    </span>
                    {isSelf && <span className="adet-lux-you-tag">Seu lance</span>}
                    {isJustArrived && <span className="adet-lux-new-tag">AGORA</span>}
                  </div>
                  <div className="adet-lux-bid-details">
                    <span className="adet-lux-bid-amount">{formatPrice(bid.amount)}</span>
                    <span className="adet-lux-bid-time">{formatDateTime(bid.timestamp)}</span>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="adet-lux-no-bids">Ainda não há lances neste leilão</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuctionDetailLux;
